import type { Ability } from "../rules/types.ts";

/**
 * The strips an ability plays (ADR-073's amendment "the Strike combo and the Heavy Strike chain", items 2 to 4): which
 * step of its combo a use plays, decided by the map server and predicted by the page with the same function, and the
 * strips its combo or chain names. Pure and allocation-free but for `stripsOf` and `chainStrips`, which a page or a test
 * calls outside a frame.
 */

/**
 * The step a caster holds when its last used ability was not this one: the next use plays the first strip.
 */
export const NO_COMBO_STEP = -1;

/**
 * The step of its combo a use plays: the step after the last one, wrapping, when the last used ability was this one
 * and this use comes no later than that use's ready tick plus the grace; otherwise the first.
 *
 * @param previous The step the caster's last use of this ability played, `NO_COMBO_STEP` when its last used ability was
 *   another, or none.
 * @param previousReady The ready tick that use's `used` carried.
 * @param tick The tick this use resolves at.
 * @param graceTicks The combo's grace.
 * @param strips How many strips the combo holds, at least 1.
 * @returns The step, 0 to `strips - 1`.
 * @example
 * comboStep(0, 110, 120, 10, 2);
 * // 1: within the grace, the second strip
 * comboStep(0, 110, 121, 10, 2);
 * // 0: a pause, the first strip again
 */
export function comboStep(previous: number, previousReady: number, tick: number, graceTicks: number, strips: number): number {
  if (previous < 0 || tick > previousReady + graceTicks) {
    return 0;
  }
  return (previous + 1) % strips;
}

/**
 * The strip a step of an ability's combo plays: its combo's strip at the step, wrapping, or its own strip when it has
 * no combo.
 *
 * @param ability The ability.
 * @param step The step, from 0.
 * @returns The strip.
 */
export function stripOfStep(ability: Pick<Ability, "strip" | "combo">, step: number): string {
  const strips = ability.combo?.strips;
  return strips === undefined ? ability.strip : (strips[step % strips.length] as string);
}

/**
 * The strips an ability's chain plays, one a blow in order; none for an ability with no chain.
 *
 * @param ability The ability.
 * @returns The strips.
 */
export function chainStrips(ability: Pick<Ability, "chain">): string[] {
  return ability.chain === null ? [] : ability.chain.hits.map((hit) => hit.strip);
}

/**
 * Every strip an ability can play, its own first, then its combo's and its chain's, each once.
 *
 * @param ability The ability.
 * @returns The strips.
 */
export function stripsOf(ability: Pick<Ability, "strip" | "combo" | "chain">): string[] {
  return [...new Set([ability.strip, ...(ability.combo?.strips ?? []), ...chainStrips(ability)])];
}
